import { TicketPriority, TicketStatus } from '../types/ticket';
import api, { PageRes, apiRequest } from './api';

export enum MessageType {
    SYSTEM = "system",
    USER = "user",
    AGENT = "agent",
    PRIVATE_NOTE = "private_note"
}

export interface Ticket {
  _id: string;
  protocolId: string;
  title: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  clientId: string;
  projectId: string;
  contactId: string;
  teamId: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  clientName: string;
  clientTags?: string[];
  projectName: string;
  contactName: string;
  contactPhone?: string;
  contactEmail?: string;
  contactTags?: string[];
  openTime: number;
  lastResponseDate: string;
  tags: string[];
  agentId?: string;
  memberIds?: string[];
  /** SLA de resposta (response_time) resolvida para este ticket, se houver uma configurada para o Projeto+prioridade. */
  firstResponseSlaId?: string;
  firstResponseDueAt?: string;
  firstResponseRiskAt?: string;
  firstResponseAt?: string;
  agentLastReadAt?: string;
  contactLastReadAt?: string;
  /** Nome do agente responsável (resolvido na listagem) */
  agentName?: string;
  /** Pessoas vinculadas ao ticket (memberIds resolvidos) */
  members?: Array<{ _id: string; name: string }>;
  /** Última mensagem visível (não conta nota privada) — resolvida na listagem */
  lastMessage?: {
    authorType: "agent" | "client" | "system";
    authorName?: string;
    preview: string;
    createdAt: string;
    /** Se o lado que NÃO escreveu essa mensagem já abriu o ticket depois dela */
    read: boolean;
  };
}

export interface TicketAttachmentRef {
  url: string;
  name: string;
  mimeType: string;
  size?: number;
  transcript?: string;
}

export interface TicketMessage {
  _id: string;
  ticketId: string;
  content: {
    msg?: string;
    attachments?: TicketAttachmentRef[];
  };
  type: MessageType;
  createdAt: string;
  /** Preenchido quando o autor edita o texto depois de enviar. */
  editedAt?: string;
  /** Preenchido quando o autor exclui a mensagem — content.msg fica vazio, vira "Mensagem excluída". */
  deletedAt?: string;
  agentId?: string;
  contact: {
    _id: string;
    name: string;
    phone: string;
  };
  agent: {
    _id: string;
    name: string;
    email: string;
  };
}

export interface TicketFormData {
  subject: string;
  description: string;
  priority: Ticket['priority'];
  clientId?: string;
  projectId?: string;
  contactId?: string;
  slaId?: string;
  teamId?: string;
  agentId?: string;
  tags?: string[];
  status?: string;
  resolutionTime?: number;
}

export interface MessagesResponse {
    result: {
        _id?: string;
        content: {
            msg?: string;
            attachments?: TicketAttachmentRef[];
        };
        type: string;
        createdAt: string;
        editedAt?: string;
        deletedAt?: string;
        contact?: {
            _id: string;
            name: string;
            phone: string;
        };
        agent?: {
            _id: string;
            name: string;
            email: string;
        };
    }[];
    total: number;
    qtdPages: number;
}

export interface SendMessageResponse {
    success: boolean;
    result: {
        _id: string;
        ticketId: string;
        content: {
            msg: string;
        };
        type: MessageType;
        createdAt: string;
        editedAt?: string;
        deletedAt?: string;
        contact: {
            _id: string;
            name: string;
            phone: string;
        };
        agent: {
            _id: string;
            name: string;
            email: string;
        };
    };
}

interface TicketParams {
  page?: number;
  limit?: number;
  s_and?: string;
  status?: string[];
  priority?: string[];
  teamId?: string[];
  agentId?: string[];
  clientId?: string[];
  clientTags?: string[];
  projectId?: string[];
}

const ticketService = {
  /** Tags que existem de verdade em tickets — separado do pool de tags de empresa/cliente. */
  async getTicketTags(): Promise<[string[] | null, any]> {
    const [data, err] = await apiRequest<{ result: string[] }>({
      loaderKey: 'ticketTags',
      method: 'get',
      url: '/app/tickets/tags',
    });
    if (err || !data) return [null, err];
    return [data.result || [], null];
  },

  // Obter lista de tickets
  async getAll(params?: TicketParams): Promise<[PageRes<Ticket> | null, any]> {
    return apiRequest<PageRes<Ticket>>({
      loaderKey: 'tickets',
      method: 'get',
      url: '/app/tickets',
      params
    });
  },

  // Obter um ticket pelo ID
  async getById(ticketId: string): Promise<[Ticket | null, any]> {
    return apiRequest<Ticket>({
      loaderKey: 'ticket',
      method: 'get',
      url: `/app/tickets/${ticketId}`
    });
  },

  // Criar novo ticket
  async create(ticketData: TicketFormData): Promise<[Ticket | null, any]> {
    return apiRequest<Ticket>({
      loaderKey: 'createTicket',
      method: 'post',
      url: '/app/tickets',
      data: ticketData
    });
  },

  // Atualizar ticket existente
  async update(ticketId: string, ticketData: Partial<TicketFormData>): Promise<[Ticket | null, any]> {
    return apiRequest<Ticket>({
      loaderKey: 'updateTicket',
      method: 'put',
      url: `/app/tickets/${ticketId}`,
      data: ticketData
    });
  },

  // Obter mensagens de um ticket
  async getMessages(ticketId: string, page: number = 1, limit: number = 10): Promise<[MessagesResponse | null, any]> {
    const params: any = { page, limit };

    return apiRequest<MessagesResponse>({
      loaderKey: 'ticketMessages',
      method: 'get',
      url: `/app/tickets/${ticketId}/messages`,
      params
    });
  },

  /** Upload de arquivo para anexar à mensagem (admin/agente). */
  async uploadAttachment(file: File): Promise<[TicketAttachmentRef | null, { msg?: string } | null]> {
    const formData = new FormData();
    formData.append("file", file);
    try {
      // Instância `api` define Content-Type: application/json; sem isso o multipart não envia o arquivo.
      const res = await api.post<{ url: string; name: string; mimeType: string; size: number }>(
        "/app/tickets/upload",
        formData,
        {
          transformRequest: [
            (data, headers) => {
              if (typeof FormData !== "undefined" && data instanceof FormData) {
                const h = headers as Record<string, string> & { delete?: (k: string) => void };
                if (typeof h?.delete === "function") {
                  h.delete("Content-Type");
                } else {
                  delete h["Content-Type"];
                }
              }
              return data;
            },
          ],
        },
      );
      const d = res.data;
      return [{ url: d.url, name: d.name, mimeType: d.mimeType, size: d.size }, null];
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { msg?: string }; msg?: string } } };
      const data = err?.response?.data;
      const msg =
        data?.error?.msg ||
        (data && typeof data === "object" && "msg" in data ? (data as { msg: string }).msg : undefined) ||
        "Falha no upload";
      return [null, { msg }];
    }
  },

  async transcribeAudioFromUrl(url: string): Promise<[string | null, { msg?: string } | null]> {
    try {
      const res = await api.post<{ text: string }>("/app/tickets/ai/transcribe-url", { url });
      return [res.data.text, null];
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { msg?: string }; msg?: string } } };
      return [null, { msg: err?.response?.data?.error?.msg || err?.response?.data?.msg || "Falha na transcrição" }];
    }
  },

  // Enviar nova mensagem (texto e/ou anexos)
  async sendMessage(
    ticketId: string,
    content: string | { msg?: string; attachments?: TicketAttachmentRef[] },
    type: MessageType,
  ): Promise<[SendMessageResponse | null, any]> {
    return apiRequest<SendMessageResponse>({
      loaderKey: 'sendMessage',
      method: 'post',
      url: `/app/tickets/${ticketId}/send-message`,
      data: {
        content,
        type: type === MessageType.PRIVATE_NOTE ? MessageType.PRIVATE_NOTE : MessageType.AGENT
      }
    });
  },

  /** Edita o texto de uma mensagem própria já enviada — o backend marca como editada e só deixa
   * o próprio autor editar (nem admin edita mensagem de outro agente). */
  async editMessage(
    ticketId: string,
    messageId: string,
    content: string,
  ): Promise<[SendMessageResponse | null, any]> {
    return apiRequest<SendMessageResponse>({
      loaderKey: 'editMessage',
      method: 'put',
      url: `/app/tickets/${ticketId}/messages/${messageId}`,
      data: { content },
    });
  },

  /** Exclusão lógica — o backend apaga o conteúdo e marca deletedAt, a mensagem some do texto mas
   * continua na conversa como "Mensagem excluída". Só o próprio autor exclui. */
  async deleteMessage(
    ticketId: string,
    messageId: string,
  ): Promise<[SendMessageResponse | null, any]> {
    return apiRequest<SendMessageResponse>({
      loaderKey: 'deleteMessage',
      method: 'delete',
      url: `/app/tickets/${ticketId}/messages/${messageId}`,
    });
  },

  // Transferir ticket para outra equipe/agente
  async transfer(ticketId: string, teamId?: string, agentId?: string): Promise<[Ticket | null, any]> {
    return apiRequest<Ticket>({
      loaderKey: 'transferTicket',
      method: 'put',
      url: `/app/tickets/${ticketId}/transfer`,
      data: { teamId, agentId }
    });
  },

  // Atualizar SLA do ticket
  async updateSLA(ticketId: string, clientId: string, projectId: string, slaId: string): Promise<[Ticket | null, any]> {
    return apiRequest<Ticket>({
      loaderKey: 'updateTicketSLA',
      method: 'put',
      url: `/app/tickets/${ticketId}/sla`,
      data: { clientId, projectId, slaId }
    });
  },

  // Adicionar pessoas (participantes) ao ticket
  async addMembers(ticketId: string, userIds: string[]): Promise<[Ticket | null, any]> {
    return apiRequest<Ticket>({
      loaderKey: 'addTicketMembers',
      method: 'post',
      url: `/app/tickets/${ticketId}/members`,
      data: { userIds }
    });
  },

  // Remover uma pessoa (participante) do ticket
  async removeMember(ticketId: string, userId: string): Promise<[Ticket | null, any]> {
    return apiRequest<Ticket>({
      loaderKey: 'removeTicketMember',
      method: 'delete',
      url: `/app/tickets/${ticketId}/members/${userId}`
    });
  }
};

export default ticketService; 